import React from "react"
import useId from "react-use-uuid"
import styled from "styled-components"
import { Typography } from "@mui/material"

const Wrapper = styled.div<{ size: number; extended: boolean }>`
  width: ${p => p.size}px;
  height: ${p => p.size + (p.extended ? 40 : 0)}px;

  > p {
    text-align: center;
    font-size: ${p => (p.size <= 64 ? 0.8 : 1)}rem;
  }
`

interface ISpinnerProps {
  /**
   * Width and height of the spinner graphic
   */
  size?: number
  /**
   * Additional styles to be added to the wrapper for layout purposes
   * @default {}
   */
  styles?: React.CSSProperties
  /**
   * A small title to be displayed underneath the spinner
   * @default ""
   */
  title?: string
}
export default function Spinner({
  size = 64,
  styles = {},
  title = ""
}: ISpinnerProps) {
  const id: string = useId()
  const ringStyles = {
    inner: {
      fill: `url(#linear-gradient-3--${id})`,
      mask: `url(#mask-inner--${id})`
    },
    mid: {
      fill: `url(#linear-gradient--${id})`,
      mask: `url(#mask-mid--${id})`
    },
    outer: {
      fill: `url(#linear-gradient-2--${id})`,
      mask: `url(#mask-outer--${id})`
    }
  }
  return (
    <Wrapper
      size={size}
      extended={!!(title && title.length)}
      style={{ ...styles }}
      title=""
    >
      <svg
        xmlns="http://www.w3.org/2000/svg"
        xmlnsXlink="http://www.w3.org/1999/xlink"
        viewBox="50 50 200 200"
      >
        <path
          {...ringStyles.outer}
          d="M134.05,239a29.46,29.46,0,0,1-14.94-3.83 c-11.77-6.8-17.76-21.77-16.87-42.17,0.87-20,8.23-42.86,20.72-64.5S151.62,89,168.46,78.31 c17.22-11,33.19-13.27,45-6.48S231.18,93.6,230.29,114 c-0.87,20-8.23,42.86-20.72,64.5S180.92,218,164.07,228.69C153.34,235.53,143.1,239,134.05,239ZM198.46,71.8 c-8.3,0-17.85,3.27-28,9.71-16.34,10.41-32.05,27.77-44.24,48.89s-19.37,43.4-20.22,62.76c-0.83,18.91,4.49,32.66,15,38.71s25,3.78,41-6.39c16.34-10.41,32.05-27.77,44.24-48.89,25-43.22,27.3-88.73,5.25-101.47A25.75,25.75,0,0,0,198.46,71.8Z"
        />
        <path
          {...ringStyles.mid}
          d="M104.31,205.91 c-10.46,0-18.18-3.14-22.26-9.23-5.57-8.31-3.5-20.8,5.82-35.19,8.94-13.8,23.6-27.93,41.27-39.78,37.66-25.27,76-31.91,87.23-15.14h0 c11.25,16.77-9.43,49.71-47.09,75-17.67,11.86-36.3,20.06-52.46,23.1A68,68,0,0,1,104.31,205.91Zm90-103.76 c-16.25,0-39.93,8.43-62.45,23.54-17.14,11.5-31.32,25.14-39.92,38.41C83.8,176.59,81.66,187.49,86,194s15.27,8.67,29.9,5.92 c15.54-2.93,33.53-10.87,50.68-22.37,34.25-23,55.21-54.26,45.78-68.32C209.17,104.43,202.72,102.14,194.27,102.14Z"
        />
        <path
          {...ringStyles.inner}
          d="M92.29,170.82c-12.48,0-21.76-2.67-23.6-9.1C67.05,156,72,149.2,82.92,142.15c10.07-6.49,24.16-12.5,39.68-16.93s30.69-6.77,42.64-6.59c13,0.2,20.78,3.35,22.42,9.1h0c1.64,5.75-3.28,12.52-14.23,19.57-10.07,6.49-24.16,12.5-39.68,16.93C119.54,168.3,104.41,170.82,92.29,170.82Zm71.59-47.7c-11.34,0-25.49,2.27-40,6.42-15.1,4.32-28.77,10.14-38.48,16.39C76.76,151.47,72,157,73,160.49c2.05,7.18,27,8.71,59.5-.57,15.1-4.32,28.77-10.14,38.48-16.39,8.59-5.54,13.32-11.11,12.34-14.56h0c-1-3.44-7.94-5.68-18.17-5.84h-1.29Z"
        />

        <defs>
          <linearGradient
            id={`linear-gradient--${id}`}
            x1="112.02"
            y1="216.07"
            x2="186.43"
            y2="87.2"
            gradientUnits="userSpaceOnUse"
          >
            <stop offset="0" stopColor="#095a5a" />
            <stop offset="0.12" stopColor="#0c635d" />
            <stop offset="0.31" stopColor="#167b65" />
            <stop offset="0.56" stopColor="#24a373" />
            <stop offset="0.75" stopColor="#32c77f" />
            <stop offset="1" stopColor="#43fda2" />
          </linearGradient>
          <linearGradient
            id={`linear-gradient-2--${id}`}
            x1="119.11"
            y1="235.17"
            x2="213.42"
            y2="71.83"
            xlinkHref={`#linear-gradient--${id}`}
          />
          <linearGradient
            id={`linear-gradient-3--${id}`}
            x1="104.72"
            y1="185.35"
            x2="151.63"
            y2="104.1"
            xlinkHref={`#linear-gradient--${id}`}
          />
          <linearGradient id={`grad--${id}`} gradientTransform="rotate(90)">
            <stop offset="5%" stopColor="white" />
            <stop offset="20%" stopColor="black" />
            <stop offset="50%" stopColor="black" />
            <stop offset="80%" stopColor="black" />
            <stop offset="95%" stopColor="white" />
          </linearGradient>
          <mask id={`mask-inner--${id}`}>
            <rect width="400" height="400" fill="white" x="30" y="0" />
            <rect
              width="200"
              height="150"
              fill={`url(#grad--${id})`}
              x="0"
              y="145"
            >
              <animateTransform
                attributeName="transform"
                attributeType="XML"
                type="rotate"
                values="0 130 150; 360 130 150"
                dur="1s"
                repeatCount="indefinite"
              />
            </rect>
          </mask>
          <mask id={`mask-mid--${id}`}>
            <rect width="400" height="400" fill="white" x="0" y="0" />
            <rect
              width="200"
              height="150"
              fill={`url(#grad--${id})`}
              x="50"
              y="145"
            >
              <animateTransform
                attributeName="transform"
                attributeType="XML"
                type="rotate"
                values="0 150 150; 360 150 150"
                dur="1.4s"
                repeatCount="indefinite"
              />
            </rect>
          </mask>
          <mask id={`mask-outer--${id}`}>
            <rect width="400" height="400" fill="white" x="0" y="0" />
            <rect
              width="200"
              height="150"
              fill={`url(#grad--${id})`}
              x="50"
              y="145"
            >
              <animateTransform
                attributeName="transform"
                attributeType="XML"
                type="rotate"
                values="0 150 150; 360 150 150"
                dur="1.8s"
                repeatCount="indefinite"
              />
            </rect>
          </mask>
        </defs>
      </svg>
      {title ? <Typography>{title.substring(0, 20)}</Typography> : ""}
    </Wrapper>
  )
}
